import yaml from 'js-yaml'
import { getAuthConfig, resolveProviderCredentials, type AuthConfig, type RawAuthBlock } from '@cloud-native/www-auth'

interface Settings {
  basepath: string
  domain: string
  audience: string
  organizationId: string
  clientId: string
  accountsApiUrl: string
  workspaceUrlTemplate: string
  localStorageOrgIdKey: string
  allScopes: string
  cartoVersion: string
  isSnowflakeNativeApp?: boolean
  isSelfHosted?: boolean
  workspaceUrl?: string
  customTenant?: string
  studentClientId: string
  loginAsStudentKey: string
  supportEmail: string
  supportEmailMailto: string
  documentationUrl: string
  enableTrackJs?: boolean
  enableHubspot?: boolean
  hideCartoDatawarehouse?: boolean
  extPartner: string
  redirectAfterLoginUri: string
  autojoinAccountKey: string
  disabledSsoKey: string
  authenticationFlowKey: string
  commitHash: string
  auth: AuthConfig
}

const settings: Settings = {
  basepath: import.meta.env.VITE_APP_BASE_PATH || '',
  domain: '',
  audience: '',
  organizationId: '',
  clientId: '',
  accountsApiUrl: '',
  workspaceUrlTemplate: '{tenantDomain}',
  allScopes:
    'openid profile email read:current_user update:current_user read:connections write:connections read:maps write:maps read:account admin:account',
  cartoVersion: '',
  localStorageOrgIdKey: 'orgId',
  redirectAfterLoginUri: 'redir',
  authenticationFlowKey: 'authenticationFlow',
  autojoinAccountKey: 'autojoinAcc',
  studentClientId: 'clientId',
  loginAsStudentKey: 'student_account',
  supportEmail: 'support@carto.com',
  supportEmailMailto: 'mailto:support@carto.com',
  documentationUrl: 'https://docs.carto.com',
  extPartner: 'ext_partner',
  disabledSsoKey: 'disabledSso',
  commitHash: '',
  // Overwritten by loadConfig() from config.yaml.
  auth: {
    provider: 'auth0',
    domain: '',
    audience: '',
    scope: ''
  }
}

export function configureApiUrls(tenantDomain: string): void {
  settings.workspaceUrl = settings.workspaceUrlTemplate.replace('{tenantDomain}', tenantDomain)
}

export interface YamlConfig {
  cartoVersion: {
    selfHosted?: string
    snowflakeNativeApp?: boolean
  }
  auth: RawAuthBlock
  apis: {
    accountsUrl: string
  }
  workspaceUrlTemplate: string
  customTenant: string
  enableTrackJs?: boolean
  enableHubspot?: boolean
  cartoDwEnabled?: boolean
  commitHash: string
}

export async function loadConfig() {
  const configUrl = import.meta.env.VITE_APP_CONFIG_URL || `${import.meta.env.VITE_APP_BASE_PATH || ''}/config.yaml`

  let yamlText
  try {
    const response = await fetch(configUrl)

    if (!response.ok) {
      throw new Error(`File status code ${response.status}.`)
    }
    yamlText = await response.text()
    if (!yamlText) {
      throw new Error(`Empty config file detected.`)
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : ''
    throw new Error(`Cannot download file ${configUrl}. ${msg}`)
  }

  let config
  try {
    config = yaml.load(yamlText) as YamlConfig
  } catch (e) {
    const msg = e instanceof Error ? e.message : ''
    throw new Error(`Error parsing config file. ${msg}`)
  }

  const { apis, cartoVersion } = config

  // Validates the auth shape up front so a malformed config.yaml fails clearly.
  settings.auth = getAuthConfig(config, {
    scope: settings.allScopes,
    oidcPostLogoutRedirectUri: `${window.location.origin}${settings.basepath}`
  })

  const { clientId, domain, audience, organizationId, studentClientId } = resolveProviderCredentials(config.auth)
  settings.clientId = clientId
  settings.domain = domain
  settings.audience = audience
  settings.organizationId = organizationId
  settings.studentClientId = studentClientId

  // APIs
  settings.accountsApiUrl = apis.accountsUrl

  // WORKSPACE
  settings.workspaceUrlTemplate = config.workspaceUrlTemplate

  // ON-PREM
  settings.customTenant = config.customTenant || undefined

  // TrackJS
  settings.enableTrackJs = import.meta.env.PROD && config.enableTrackJs

  // Hubspot
  settings.enableHubspot = config.enableHubspot

  // CARTO Version
  const selfHosted = cartoVersion.selfHosted
  settings.cartoVersion = selfHosted ? `${selfHosted}` : 'SaaS'
  settings.isSelfHosted = settings.cartoVersion !== 'SaaS'
  settings.isSnowflakeNativeApp = cartoVersion.snowflakeNativeApp || false

  // CARTO Datawarehouse
  settings.hideCartoDatawarehouse = !config.cartoDwEnabled
}

// True in a self-hosted disconnected deployment, where auth is served by the internal
// auth-api (OIDC) rather than Auth0. The frontend twin of the backend's Internal mode.
export function isDisconnected(): boolean {
  return settings.auth.provider === 'oidc'
}

export default settings
